import { gql, TypedDocumentNode } from '@apollo/client';

import {
  AssetAttributeChangeInput,
  AssetAttributesUpdateInput,
  AssetChangesApprovalInput,
  AssetChangeTypesInput,
  AssetCreateInput,
  AssetDeleteInput,
  AssetDetail,
  AssetElementsCreateInput,
  AssetNodeChangeState,
  AssetRouteAutocomplete,
  AssetRouteSimpleInput,
  AssetsConnection,
  AssetsConnectionInput,
  AssetsWithElementsConnection,
  AssetsWithElementsInput,
  AssetUpdateInput,
  AssetUsageConnection,
  AssetUsageConnectionInput,
  AssetUsageSummary,
  MoveElementInput,
  ReplaceElementInput,
  SearchAssetsConnection,
  SearchAssetsConnectionInput,
  SearchAssetsWithElementsConnection,
  SearchAssetsWithElementsConnectionInput,
} from '@atom/types/asset';

export const BASE_ASSET_ELEMENT = gql`
  fragment BaseAssetElement on AssetElement {
    id
    name
    assetType
    schemaId
    markerId
    hasChildren
    changeType
    hasDescendantChanges
    includeChangesBefore
    attributes {
      id
      name
      value
      unit
      dataType
      isVisibleAsSubtext
    }
  }
`;

export const BASE_ASSET = gql`
  fragment BaseAsset on Asset {
    id
    name

    address
    externalId
    categoryId
    assetType
    creationType
    schemaId
    parentAssetId
    colorId
    markerId
    isMaterial
    mainPhotoFileId
    inactive
    archived
    ancestors {
      id
      name
    }

    changeType
    includeChangesBefore

    lastAppliedWorkOrderName
    lastAppliedDate
    lastWorkOrder {
      id
      name
      updatedDate
    }

    location {
      coordinates
      type
    }
    locationType

    attributeGroups {
      id
      name
      expandByDefault
      attributes {
        id
        name
        value
        newValue
        changeId
        defaultValue
        enumeration
        isEditable
        isRequired
        unit
        group
        order
        dataType
        thousandsSeparator
        isVisibleAsSubtext
      }
    }

    budget {
      isStockBased
      type
      isStartEndReading
      isStartEndCalculated
      quantityOnHand
      rate
      unit
      conversionUnits {
        id
        unit
        conversionNumber
        active
        isDefault
      }
    }

    categories {
      id
      name
    }

    createdDate
    createdBy {
      firstName
      lastName
    }
    updatedDate
    updatedBy {
      firstName
      lastName
    }

    actions
    grants {
      type
      ids
    }

    cursor {
      total
      index
      next
      previous
    }
    elements {
      ...BaseAssetElement
    }
    elementGroups {
      id
      name
      elements {
        ...BaseAssetElement
      }
    }
  }
  ${BASE_ASSET_ELEMENT}
`;

export const GET_ASSET: TypedDocumentNode<
  { asset: AssetDetail },
  { id: string; includeCursor?: boolean }
> = gql`
  query asset($id: ID!, $includeCursor: Boolean) {
    asset(id: $id, includeCursor: $includeCursor) {
      ...BaseAsset
    }
  }
  ${BASE_ASSET}
`;

export const GET_ASSETS: TypedDocumentNode<
  { assets: AssetsConnection },
  { input: AssetsConnectionInput }
> = gql`
  query assets($input: AssetsConnectionInput!) {
    assets(input: $input) {
      totalCount
      assets {
        id
        name
        assetType
        schemaId
        colorId
        markerId
        mainPhotoFileId
        attributes
        archived
        categories {
          id
          name
        }
        budget {
          isStartEndReading
          isStartEndCalculated
          quantityOnHand
          rate
          unit
          conversionUnits {
            id
            unit
            conversionNumber
            active
            isDefault
          }
        }
        location {
          coordinates
          type
        }
        createdDate
        createdBy {
          firstName
          lastName
        }
        updatedDate
        updatedBy {
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_ASSETS_WITH_ELEMENTS: TypedDocumentNode<
  { assetsWithElements: AssetsWithElementsConnection },
  { input: AssetsWithElementsInput }
> = gql`
  query assetsWithElements($input: AssetsWithElementsInput!) {
    assetsWithElements(input: $input) {
      totalCount
      assets {
        id
        name
        assetType
        markerId
        schemaId
        attributes
        attributeGroups {
          id
          name
          expandByDefault
          attributes
        }
        elements
        elementGroups {
          id
          name
          elements
        }
      }
    }
  }
`;

export const SEARCH_ASSETS: TypedDocumentNode<
  { assetsSearch: SearchAssetsConnection },
  { input: SearchAssetsConnectionInput }
> = gql`
  query assetsSearch($input: SearchAssetsConnectionInput!) {
    assetsSearch(input: $input) {
      totalCount
      assets {
        id
        schemaId
        externalId
        name
        assetType
        markerId
        colorId
        categoryPath
        isMaterial
        locationType
        inactive
        archived
        budget {
          isStockBased
          rate
          unit
        }
      }
    }
  }
`;

export const SEARCH_ASSETS_WITH_ELEMENTS: TypedDocumentNode<
  { assetsWithElementsSearch: SearchAssetsWithElementsConnection },
  { input: SearchAssetsWithElementsConnectionInput }
> = gql`
  query assetsWithElementsSearch(
    $input: SearchAssetsWithElementsConnectionInput!
  ) {
    assetsWithElementsSearch(input: $input) {
      totalCount
      assets {
        id
        schemaId
        externalId
        name
        assetType
        markerId
        colorId
        categoryPath
        isMaterial
        locationType
        inactive
        budget {
          isStockBased
          rate
          unit
        }
        isSubItem
        ancestors {
          id
          name
        }
        categories {
          id
          name
        }
        attributes
        attributeGroups {
          id
          name
          expandByDefault
          attributes
        }
      }
    }
  }
`;

export const MOVE_ELEMENT: TypedDocumentNode<
  { moveElement: boolean },
  { input: MoveElementInput }
> = gql`
  mutation moveElement($input: MoveElementInput!) {
    moveElement(input: $input)
  }
`;

export const REPLACE_ELEMENT: TypedDocumentNode<
  { replaceElement: boolean },
  { input: ReplaceElementInput }
> = gql`
  mutation replaceElement($input: ReplaceElementInput!) {
    replaceElement(input: $input)
  }
`;

export const CREATE_ASSET: TypedDocumentNode<
  { assetCreate: AssetDetail },
  { input: AssetCreateInput }
> = gql`
  mutation assetCreate($input: AssetCreateInput!) {
    assetCreate(input: $input) {
      ...BaseAsset
    }
  }
  ${BASE_ASSET}
`;

export const ASSET_ROUTE_AUTOCOMPLETE: TypedDocumentNode<
  { assetRouteAutocomplete: AssetRouteAutocomplete },
  { input: AssetRouteSimpleInput }
> = gql`
  mutation assetRouteAutocomplete($input: AssetRouteSimpleInput!) {
    assetRouteAutocomplete(input: $input) {
      components {
        title
        dataType
        enumeration
        value
      }
      milePostRanges
      snappedGeometry
    }
  }
`;

export const UPDATE_ASSET: TypedDocumentNode<
  { assetUpdate: AssetDetail },
  { input: AssetUpdateInput; includeCursor?: boolean }
> = gql`
  mutation assetUpdate($input: AssetUpdateInput!, $includeCursor: Boolean) {
    assetUpdate(input: $input, includeCursor: $includeCursor) {
      ...BaseAsset
    }
  }
  ${BASE_ASSET}
`;

export const UPDATE_ASSET_ATTRIBUTES: TypedDocumentNode<
  { assetAttributesUpdate: boolean },
  { input: AssetAttributesUpdateInput }
> = gql`
  mutation assetAttributesUpdate($input: AssetAttributesUpdateInput!) {
    assetAttributesUpdate(input: $input)
  }
`;

export const DELETE_ASSET: TypedDocumentNode<
  { assetDelete: boolean },
  { input: AssetDeleteInput }
> = gql`
  mutation assetDelete($input: AssetDeleteInput!) {
    assetDelete(input: $input)
  }
`;

export const APPROVE_ASSET_CHANGES: TypedDocumentNode<
  { assetChangesApprove: boolean },
  { input: AssetChangesApprovalInput }
> = gql`
  mutation assetChangesApprove($input: AssetChangesApprovalInput!) {
    assetChangesApprove(input: $input)
  }
`;

export const REJECT_ASSET_CHANGES: TypedDocumentNode<
  { assetChangesReject: boolean },
  { input: AssetChangesApprovalInput }
> = gql`
  mutation assetChangesReject($input: AssetChangesApprovalInput!) {
    assetChangesReject(input: $input)
  }
`;

export const APPROVE_ASSET_ATTRIBUTE_CHANGE: TypedDocumentNode<
  { assetAttributeChangeApprove: boolean },
  { input: AssetAttributeChangeInput }
> = gql`
  mutation assetAttributeChangeApprove($input: AssetAttributeChangeInput!) {
    assetAttributeChangeApprove(input: $input)
  }
`;

export const REJECT_ASSET_ATTRIBUTE_CHANGE: TypedDocumentNode<
  { assetAttributeChangeReject: boolean },
  { input: AssetAttributeChangeInput }
> = gql`
  mutation assetAttributeChangeReject($input: AssetAttributeChangeInput!) {
    assetAttributeChangeReject(input: $input)
  }
`;

export const CREATE_ASSET_ELEMENTS: TypedDocumentNode<
  { assetElementsCreate: boolean },
  { input: AssetElementsCreateInput }
> = gql`
  mutation assetElementsCreate($input: AssetElementsCreateInput!) {
    assetElementsCreate(input: $input)
  }
`;

export const GET_ASSET_USAGE: TypedDocumentNode<
  { assetUsage: AssetUsageConnection },
  { input: AssetUsageConnectionInput }
> = gql`
  query assetUsage($input: AssetUsageConnectionInput!) {
    assetUsage(input: $input) {
      totalCount
      usage {
        id
        name
        statusId
        dueDate
        completionDate
        totalUsage
        totalCost
      }
    }
  }
`;

export const GET_ASSET_USAGE_SUMMARY: TypedDocumentNode<
  { assetUsageSummary: AssetUsageSummary },
  { assetId: string }
> = gql`
  query assetUsageSummary($assetId: ID!) {
    assetUsageSummary(assetId: $assetId) {
      totalUsage
      totalUsageToday
      totalRemaining
      quantityOnHand
      totalCost
      totalCostYear
      totalCostMonth
      rate
    }
  }
`;

export const GET_ASSET_CHANGE_TYPES: TypedDocumentNode<
  { assetChangeTypes: AssetNodeChangeState[] },
  { input: AssetChangeTypesInput }
> = gql`
  query assetChangeTypes($input: AssetChangeTypesInput!) {
    assetChangeTypes(input: $input) {
      assetId
      changeType
      hasDescendantChanges
    }
  }
`;
